import {
  ChangeEvent,
  FC,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';

import ArrowDownIcon from 'assets/icons/arrow-down.svg';
import CloseIcon from 'assets/icons/close.svg';
import assistantAvatar from 'assets/images/assistant.jpg';
import { Modal } from 'components/Modal/Modal';
import { store } from 'store';
import { handleAction } from 'store/assistant/actionHandler';
import { TEXT_WS_URL } from 'store/assistant/consts';
import {
  buildWsUrl,
  handleAssistantWsEvent,
  logSessionInit,
  logWsClosed,
  logWsConnected,
  logWsConnecting,
  logWsError,
  sendTextSessionUpdate,
} from 'store/assistant/wsHandler';

import * as S from './TextChat.styles';

const TYPEWRITER_SPEED = 15;
const TYPEWRITER_BATCH = 3;
const SCROLL_BOTTOM_THRESHOLD = 50;
const TEXTAREA_MAX_HEIGHT = 120;

// ── Typewriter effect for assistant messages ──────────────────────────────────

const AssistantText: FC<{ text: string; streaming: boolean }> = ({ text, streaming }) => {
  const wasStreaming = useRef(streaming);
  const [len, setLen] = useState(streaming ? 0 : text.length);

  if (streaming) wasStreaming.current = true;

  useEffect(() => {
    // Old finalized message (never streamed) — show full text
    if (!wasStreaming.current) {
      setLen(text.length);
      return;
    }
    // Typewriter caught up — nothing to do
    if (len >= text.length) return;

    const timer = setTimeout(() => {
      setLen((prev) => Math.min(prev + TYPEWRITER_BATCH, text.length));
    }, TYPEWRITER_SPEED);

    return () => clearTimeout(timer);
  }, [len, text]);

  return <>{text.slice(0, len)}</>;
};

// ── Main component ───────────────────────────────────────────────────────────

export const TextChat: FC = observer(() => {
  const {
    isChatOpen,
    chatState,
    chatMessages,
    setChatState,
    addChatMessage,
    ensureUserId,
    buildInstructions,
    touchLastInteraction,
    ensureAssistantMsg,
    saveMessage,
    setAssistantMode,
  } = store.assistantStore;

  const [input, setInput] = useState('');
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isAtBottomRef = useRef(true);
  const liveSessionRef = useRef(false);

  // ── Scroll helpers ────────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
    setShowScrollDown(false);
  }, []);

  const handleScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD;
    isAtBottomRef.current = atBottom;
    if (atBottom) setShowScrollDown(false);
    else setShowScrollDown(true);
  }, []);

  // Auto-scroll when at bottom and content changes
  useEffect(() => {
    if (isAtBottomRef.current) {
      const el = messagesRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  });

  // ── Connect WS on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isChatOpen || chatState !== 'idle') return;

    setChatState('connecting');
    const userId = ensureUserId();

    logWsConnecting(TEXT_WS_URL);
    const ws = new WebSocket(buildWsUrl(TEXT_WS_URL, userId));
    wsRef.current = ws;

    ws.onopen = () => {
      logWsConnected();
      const sophie = buildInstructions();
      logSessionInit(sophie);

      sendTextSessionUpdate(ws, userId, sophie.userName, sophie.instructions);

      // Always trigger Sophie's first message in text mode.
      ensureAssistantMsg();
      ws.send(JSON.stringify({ type: 'text_message', text: '[greeting]' }));

      touchLastInteraction();
      setChatState('ready');
    };

    ws.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        handleAssistantWsEvent(event, {
          onModeSwitch: liveSessionRef.current
            ? (mode) => store.commonStore.setMode(mode)
            : undefined,
          onAction: liveSessionRef.current ? handleAction : undefined,
        });
      } catch (e) {
        console.log('[TextChat] Parse error:', e);
      }
    };

    ws.onerror = (err) => {
      logWsError((err as ErrorEvent).message);
      setChatState('error');
    };

    ws.onclose = (ev) => {
      logWsClosed(ev.code, ev.reason);
      if (chatState !== 'idle') {
        setChatState('idle');
      }
    };

    return () => {
      ws.close();
      wsRef.current = null;
      liveSessionRef.current = false;
      setChatState('idle');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatOpen]);

  // ── Send message ────────────────────────────────────────────────────────

  const doSend = useCallback(() => {
    const text = input.trim();
    if (!text || chatState !== 'ready' || !wsRef.current) return;

    liveSessionRef.current = true;
    addChatMessage({ id: 'u_' + Date.now(), role: 'user', text, ts: Date.now() });
    saveMessage('user', text);
    setInput('');

    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Prepare assistant placeholder
    ensureAssistantMsg();
    wsRef.current.send(JSON.stringify({ type: 'text_message', text }));

    // Scroll to bottom after sending
    isAtBottomRef.current = true;
  }, [input, chatState, addChatMessage, ensureAssistantMsg]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      doSend();
    },
    [doSend]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        doSend();
      }
    },
    [doSend]
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT) + 'px';
  }, []);

  if (!isChatOpen) return null;

  const isOnline = chatState === 'ready';
  const statusText =
    chatState === 'connecting'
      ? 'connecting...'
      : chatState === 'error'
        ? 'connection error'
        : chatState === 'ready'
          ? 'online'
          : 'offline';

  return (
    <>
      <S.Overlay>
        <S.Panel>
          <S.Header>
            <S.AvatarWrap onClick={() => setShowPhoto(true)}>
              <S.Avatar>
                <img src={assistantAvatar} alt="Sophie" />
              </S.Avatar>
              {isOnline && <S.OnlineDot />}
            </S.AvatarWrap>
            <S.HeaderInfo>
              <S.HeaderName>Sophie</S.HeaderName>
              <S.HeaderStatus $online={isOnline}>{statusText}</S.HeaderStatus>
            </S.HeaderInfo>
            <S.CloseBtn onClick={() => setAssistantMode(null)}>
              <CloseIcon />
            </S.CloseBtn>
          </S.Header>

          <S.MessagesArea>
            <S.Messages ref={messagesRef} onScroll={handleScroll}>
              {chatMessages.length === 0 && (
                <S.EmptyState>
                  <S.EmptyAvatar onClick={() => setShowPhoto(true)}>
                    <img src={assistantAvatar} alt="Sophie" />
                  </S.EmptyAvatar>
                  <span>Chat with Sophie</span>
                </S.EmptyState>
              )}
              {chatMessages.map((msg) => (
                <S.MessageRow key={msg.id} $isUser={msg.role === 'user'}>
                  {msg.role === 'assistant' && (
                    <S.MessageAvatar onClick={() => setShowPhoto(true)}>
                      <img src={assistantAvatar} alt="Sophie" />
                    </S.MessageAvatar>
                  )}
                  <S.Bubble $isUser={msg.role === 'user'}>
                    {msg.streaming && !msg.text ? (
                      <S.TypingDots>
                        <span />
                        <span />
                        <span />
                      </S.TypingDots>
                    ) : msg.role === 'assistant' ? (
                      <AssistantText text={msg.text} streaming={!!msg.streaming} />
                    ) : (
                      msg.text
                    )}
                  </S.Bubble>
                </S.MessageRow>
              ))}
            </S.Messages>

            {showScrollDown && (
              <S.ScrollDownBtn onClick={scrollToBottom}>
                <ArrowDownIcon />
              </S.ScrollDownBtn>
            )}
          </S.MessagesArea>

          <S.InputRow onSubmit={handleSubmit}>
            <S.TextArea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={chatState !== 'ready'}
              rows={1}
              autoFocus
            />
            <S.SendButton type="submit" disabled={!input.trim() || chatState !== 'ready'}>
              ➤
            </S.SendButton>
          </S.InputRow>
        </S.Panel>
      </S.Overlay>

      <Modal isOpen={showPhoto} onClose={() => setShowPhoto(false)} title="Sophie">
        <S.ProfileCard>
          <S.ProfilePhoto>
            <img src={assistantAvatar} alt="Sophie" />
          </S.ProfilePhoto>
          <S.ProfileName>Sophie</S.ProfileName>
          <S.ProfileBio>
            Your AI navigator through the blockchain universe. I help you explore DeFi, find the
            best swap routes, and never get lost in the crypto cosmos.
          </S.ProfileBio>
          <S.ProfileTags>
            <S.ProfileTag>DeFi</S.ProfileTag>
            <S.ProfileTag>Swap</S.ProfileTag>
            <S.ProfileTag>Cross-chain</S.ProfileTag>
            <S.ProfileTag>Analytics</S.ProfileTag>
          </S.ProfileTags>
        </S.ProfileCard>
      </Modal>
    </>
  );
});
